import React, { useState, useContext, useMemo } from 'react';
import { Box } from '@mui/material';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { withTranslation } from 'react-i18next';
import * as PropTypes from 'prop-types';
import TitleBar from './TitleBar';
import questions from '../utils/Questions';
import QuestionModule from './ModuleGroups/QuestionModule';
import ModuleSummary from './ModuleSummary';
import Scoring from './Scoring';
import { QUESTION_UNKNOWN_VALUE, ADAPTIVE_CAPACITY_MODULES, INSUFFICIENT_EVIDENCE, RCP_VALUES } from '../utils/Consts';
import AssessmentForm from './AssessmentForm';
import SimpleTabPanel from './SimpleTabPanel';
import ModuleA from './ModuleGroups/ModuleA';
import Footer from './Footer';
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Graphic from "@arcgis/core/Graphic";
import { useAuth } from '../AuthContext';
import classNames from 'classnames';
import './Modules.css';
import { MapViewContext } from 'react-mapcore-library';
import { exposureStatuses } from '../utils/Consts';
import Logos from './Logos';
import { calculateAllResults } from '../utils/Utils';

function Modules({ t }) {

  /* Only need to track during assessment for exporting to CSV, if changes are made to loaded assessment */
  const [lastModified, setLastModified] = useState(false);
  const [lastSaved, setLastSaved] = useState(false);

  /** Survey answers and dependent values: */
  const [surveyAnswers, setSurveyAnswers] = useState(Object.fromEntries(
    Object.entries(questions).flatMap((entry) => {
      const details = entry[1];
      return details.questions.map(
        (question) => [question.label, QUESTION_UNKNOWN_VALUE],
      );
    }),
  ));
  const changeSurveyAnswer = (surveyQuestionId, value) => {
    setSurveyAnswers((curVal) => ({ ...curVal, [surveyQuestionId]: value }));
    setLastModified(Date.now());
  };

  /* State name, mainly needed for exporting to CSV: */
  const [stateName, setStateName] = useState(null);
  const changeStateName = (newStateName) => {
    setStateName(newStateName);
    setLastModified(Date.now());
  };

  /* Assessed Form values: */

  const [assessed, setAssessed] = useState({
    geographicArea: '',
    assessor: '',
    scientificName: '',
    commonName: '',
    population: '',
    kingdom: '',
    gRank: '',
    majorTaxonomicGroup: '',
    sRank: '',
    assessmentNotes: '',
    completed: false
  });
  const changeAssessed = (key, value) => {
    setAssessed((curVal) => {
      const newVal = { ...curVal, [key]: value };
      newVal.completed = Boolean(newVal.geographicArea && newVal.scientificName && newVal.kingdom && newVal.majorTaxonomicGroup);
      return newVal;
    });
    setLastModified(Date.now());
  };

  const savedVersionOutdated = lastSaved && lastModified && lastModified > lastSaved;
  const canSave = (!lastSaved || savedVersionOutdated) && assessed.completed;

  /* Exposure and map-related values: */

  const [graphicsLayer] = React.useState(() => new GraphicsLayer({ listMode: "hide" }));
  const [exposureResults, setExposureResults] = React.useState([INSUFFICIENT_EVIDENCE, INSUFFICIENT_EVIDENCE]);
  const [exposureStatus, setExposureStatus] = React.useState(exposureStatuses.NO_AREA_TO_CALCULATE);
  const changeExposureStatus = (newStatus) => {
    setExposureStatus(newStatus);
    setLastModified(Date.now());
  };
  const {mapView} = useContext(MapViewContext);

  /* Saving and loading: */

  const [assessmentId, setAssessmentId] = React.useState(crypto.randomUUID());
  const { email } = useAuth();
  const [savedAuthor, setSavedAuthor] = React.useState(email);

  // TODO: it would make sense to save results as an array since we now store them that way in the frontend,
  // but until the backend is updated, convert exposure results to an object, before saving:
  const exposureResultsToSave = RCP_VALUES.reduce((acc, rcpValue, idx) => (
    { ...acc, [rcpValue]: exposureResults[idx] }
  ), {});

  // Most of the state of the assessment (obj that gets loaded/saved), with the exception of
  // "graphics", which is determined at the time of save:
  const globalAssessmentState = {
    ccviVersion: '0.1.0',
    assessmentId: assessmentId,
    author: savedAuthor || email,
    assessed: assessed,
    surveyAnswers: surveyAnswers,
    exposureResults: exposureResultsToSave,
    exposureStatus: exposureStatus,
    lastModified: lastModified,
    stateName: stateName
  };
  // Don't allow saving the status of "CALCULATING":
  if (exposureStatus === exposureStatuses.CALCULATING) {
    globalAssessmentState.exposureStatus = exposureStatuses.READY_TO_CALCULATE;
  }

  const setAllFormState = (globalAssessmentState) => {
    setAssessmentId(globalAssessmentState.assessmentId);
    setSavedAuthor(globalAssessmentState.author);
    setAssessed(globalAssessmentState.assessed);
    setSurveyAnswers(globalAssessmentState.surveyAnswers);
    setLastModified(globalAssessmentState.lastModified);
    setLastSaved(globalAssessmentState.lastModified);
    setStateName(globalAssessmentState.stateName);

    graphicsLayer.graphics.removeAll();
    graphicsLayer.graphics.addMany(globalAssessmentState.graphics.map((graphic) => Graphic.fromJSON(graphic)));
    setExposureResults(RCP_VALUES.map(rcpValue => Number(globalAssessmentState.exposureResults[rcpValue])));
    setExposureStatus(globalAssessmentState.exposureStatus);
    if (mapView) mapView.goTo(graphicsLayer.graphics);
  };

  /** Calculated detailed results object based on survey answers and the exposure values */
  const allResults = useMemo(() => {
    return calculateAllResults(surveyAnswers, exposureResults);
  }, [surveyAnswers, exposureResults]);

  /* Tabs: */

  const [tabIndex, setTabIndex] = React.useState(0);

  const handleTabChange = (_, newTabIndex) => {
    setTabIndex(newTabIndex);
  };

  const moduleTabsDisabled = !assessed.completed;

  let tabName = 'assessorAssessedForm';
  let tabs = [<Tab
    key={tabName}
    label={t(`tabBar.${tabName}`)}
    id={`module-tab-${tabName}`}
    aria-controls={`module-tabpanel-${tabName}`} />];
  tabName = 'module-A';
  tabs.push(<Tab
    key={tabName}
    label={t(`tabBar.${tabName}`)}
    disabled={moduleTabsDisabled}
    id={`module-tab-${tabName}`}
    aria-controls={`module-tabpanel-${tabName}`} />);
  Object.keys(questions).forEach((moduleSection) => {
    tabName = `module-${moduleSection}`;
    tabs.push(
      <Tab
        key={tabName}
        label={t(`tabBar.${tabName}`)}
        disabled={moduleTabsDisabled}
        style={{ pointerEvents: 'auto' }}
        id={`module-tab-${tabName}`}
        aria-controls={`module-tabpanel-${tabName}`} />);
  });
  tabName = 'moduleSummary';
  tabs.push(<Tab
    key={tabName}
    label={t(`tabBar.${tabName}`)}
    disabled={moduleTabsDisabled}
    style={{ pointerEvents: 'auto' }}
    id={`module-tab-${tabName}`}
    aria-controls={`module-tabpanel-${tabName}`} />);
  tabName = 'scoring';
  tabs.push(<Tab
    key={tabName}
    label={t(`tabBar.${tabName}`)}
    disabled={moduleTabsDisabled}
    style={{ pointerEvents: 'auto' }}
    id={`module-tab-${tabName}`}
    aria-controls={`module-tabpanel-${tabName}`} />);


  /* render: */

  return (
    <div className={classNames(tabIndex === 1 ? 'module-a-selected' : 'module-a-not-selected')}>
      <TitleBar
        globalAssessmentState={globalAssessmentState}
        graphicsLayer={graphicsLayer}
        setAssessmentId={setAssessmentId}
        setSavedAuthor={setSavedAuthor}
        setAllFormState={setAllFormState}
        canSave={canSave}
        onSaved={() => setLastSaved(Date.now())}
      />
      <Box>
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          variant='scrollable'
          scrollButtons='auto'
        >
          {tabs}
        </Tabs>
        <SimpleTabPanel
          value={tabIndex}
          index={0}
          role='tabpanel'
          id={'module-tabpanel-assessorAssessedForm'}
          aria-labelledby={'module-tab-assessorAssessedForm'}
        >
          <div className="content-container">
            <AssessmentForm
              assessed={assessed}
              changeAssessed={changeAssessed}
              changeSurveyAnswer={changeSurveyAnswer}
            />
          </div>
          <Logos />
        </SimpleTabPanel>
        <SimpleTabPanel
          value={tabIndex}
          index={1}
          role='tabpanel'
          id={'module-tabpanel-module-A'}
          aria-labelledby={'module-tab-module-A'}
          unmountWhenHidden={false}
        >
          <ModuleA
            t={t}
            graphicsLayer={graphicsLayer}
            exposureResults={exposureResults}
            setExposureResults={setExposureResults}
            exposureStatus={exposureStatus}
            setExposureStatus={changeExposureStatus}
            stateName={stateName}
            setStateName={changeStateName}
          />
        </SimpleTabPanel>
        {Object.keys(questions).map((moduleSection, moduleIndex) => (
          // + 1 to account for assessor/assessed form
          <SimpleTabPanel
            key={moduleSection}
            value={tabIndex}
            index={moduleIndex + 2}
            role='tabpanel'
            id={`module-tabpanel-module-${moduleSection}`}
            aria-labelledby={`module-tab-module-${moduleSection}`}
          >
            <QuestionModule
              questions={questions[moduleSection].questions}
              scale={questions[moduleSection].scale}
              assessed={assessed}
              surveyAnswers={surveyAnswers}
              changeSurveyAnswer={changeSurveyAnswer}
              isAdaptiveCapacity={ADAPTIVE_CAPACITY_MODULES.includes(moduleSection)}
              adaptiveCapacity={allResults.adaptiveCapacityInfo.score}
            />
          </SimpleTabPanel>
        ))}
        <SimpleTabPanel
          value={tabIndex}
          index={2 + Object.keys(questions).length}
          role='tabpanel'
          id={'module-tabpanel-moduleSummary'}
          aria-labelledby={'module-tab-moduleSummary'}
        >
          <div className="content-container">
            <ModuleSummary moduleCompleteness={allResults.moduleCompleteness} />
          </div>
        </SimpleTabPanel>
        <SimpleTabPanel
          value={tabIndex}
          index={3 + Object.keys(questions).length}
          role='tabpanel'
          id={'module-tabpanel-scoring'}
          aria-labelledby={'module-tab-scoring'}
        >
          <div className="content-container">
            <Scoring
              results={allResults}
              globalAssessmentState={globalAssessmentState}
              graphicsLayer={graphicsLayer}
            />
          </div>
        </SimpleTabPanel>
        <Footer tabCount={tabs.length} tabIndex={tabIndex} setTabIndex={setTabIndex} cannotContinue={moduleTabsDisabled} />
      </Box>
    </div>
  );
}

Modules.propTypes = {
  t: PropTypes.func.isRequired,
};

export default withTranslation('translations')(Modules);
